/**
 * 파트너 등록·수정 창 — 「화면 설계 › 파트너 목록·상세 ① 기본 정보」.
 * @spec:2026-10-14-phai-admin-외부연동키-관리화면
 *
 * 종류·연결 계정은 등록 때만 고른다(등록 후에는 읽기 전용). 보낼 본문은 페이지가 만든다 —
 * 이 창은 입력을 검증하고 정리한 값(`PartnerFormValues`)만 올린다.
 */
import React, { useState } from "react";
import Button from "components/atoms/Button";
import type { PartnerMeta, PartnerSummary } from "api/partnerAdminService";
import {
  codeLabel,
  normalizeDomains,
  parseLines,
  validateCallbackDomains,
  validateEmail,
  validateName,
  validateProvisionUrl,
  type PartnerFormValues,
} from "utils/partnerAdmin";
import {
  CheckLabel,
  ErrorMsg,
  Field,
  FieldLabel,
  Hint,
  ModalBody,
  ModalFooter,
  ModalTitle,
  ReadOnlyValue,
  Select,
  TextArea,
  TextInput,
} from "./style";

interface PartnerFormModalProps {
  meta: PartnerMeta;
  /** 없으면 등록, 있으면 수정 */
  partner?: PartnerSummary;
  onSubmit: (values: PartnerFormValues) => Promise<void>;
  onCancel: () => void;
}

type Errors = Partial<Record<"name" | "code" | "userEmail" | "callbackDomains" | "provisionUrl", string>>;

const PartnerFormModal: React.FC<PartnerFormModalProps> = ({ meta, partner, onSubmit, onCancel }) => {
  const isEdit = !!partner;
  const [name, setName] = useState(partner?.name ?? "");
  const [code, setCode] = useState(partner?.code ?? "");
  const [userEmail, setUserEmail] = useState(partner?.userEmail ?? "");
  const [consentProfile, setConsentProfile] = useState(partner?.consentProfile ?? "");
  const [domainsText, setDomainsText] = useState((partner?.callbackDomains ?? []).join("\n"));
  const [provisionUrl, setProvisionUrl] = useState(partner?.provisionUrl ?? "");
  const [autoReview, setAutoReview] = useState(partner?.autoReviewOnCollect ?? false);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const domains = normalizeDomains(parseLines(domainsText));
    const next: Errors = {};
    const nameCheck = validateName(name);
    if (!nameCheck.ok) next.name = nameCheck.message;
    if (!isEdit) {
      if (!code) next.code = "종류를 고르세요.";
      const emailCheck = validateEmail(userEmail);
      if (!emailCheck.ok) next.userEmail = emailCheck.message;
    }
    const domainCheck = validateCallbackDomains(domains);
    if (!domainCheck.ok) next.callbackDomains = domainCheck.message;
    const urlCheck = validateProvisionUrl(provisionUrl, domains);
    if (!urlCheck.ok) next.provisionUrl = urlCheck.message;
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        code,
        userEmail: userEmail.trim(),
        consentProfile: consentProfile || null,
        callbackDomains: domains,
        provisionUrl: provisionUrl.trim() || null,
        autoReviewOnCollect: autoReview,
      });
    } catch {
      // 실패 사유는 훅이 토스트로 보여준다. 입력을 고쳐 다시 보낼 수 있게 창은 그대로 둔다.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalBody data-testid="partner-form">
      <ModalTitle>{isEdit ? "파트너 정보 수정" : "파트너 등록"}</ModalTitle>

      <Field>
        <FieldLabel>이름</FieldLabel>
        <TextInput
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="예: 서울대 K-MRS (dev)"
          data-testid="partner-form-name"
        />
        {errors.name && <ErrorMsg>{errors.name}</ErrorMsg>}
      </Field>

      {isEdit ? (
        <>
          <Field as="div">
            <FieldLabel>종류</FieldLabel>
            <ReadOnlyValue>{codeLabel(partner?.code, meta)}</ReadOnlyValue>
          </Field>
          <Field as="div">
            <FieldLabel>연결 계정</FieldLabel>
            <ReadOnlyValue>{partner?.userEmail ?? partner?.userId}</ReadOnlyValue>
            <Hint>종류와 연결 계정은 등록 후 바꾸지 않습니다.</Hint>
          </Field>
        </>
      ) : (
        <>
          <Field>
            <FieldLabel>종류</FieldLabel>
            <Select value={code} onChange={(e) => setCode(e.target.value)} data-testid="partner-form-code">
              <option value="">고르세요</option>
              {meta.codes.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </Select>
            {errors.code && <ErrorMsg>{errors.code}</ErrorMsg>}
          </Field>
          <Field>
            <FieldLabel>연결 계정 이메일</FieldLabel>
            <TextInput
              type="email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              placeholder="파트너 요청을 처리할 PhAI 계정"
              data-testid="partner-form-email"
            />
            <Hint>환자·보고서가 이 계정 아래 생깁니다. 등록 후에는 바꿀 수 없습니다.</Hint>
            {errors.userEmail && <ErrorMsg>{errors.userEmail}</ErrorMsg>}
          </Field>
        </>
      )}

      <Field>
        <FieldLabel>동의 화면 프로필</FieldLabel>
        <Select
          value={consentProfile}
          onChange={(e) => setConsentProfile(e.target.value)}
          data-testid="partner-form-consent"
        >
          <option value="">선택 안 함</option>
          {meta.consentProfiles.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </Select>
      </Field>

      <Field>
        <FieldLabel>콜백 허용 도메인</FieldLabel>
        <TextArea
          value={domainsText}
          onChange={(e) => setDomainsText(e.target.value)}
          placeholder={"한 줄에 하나씩, 호스트명만\n예: kmrs-api.ymyd.co.kr"}
          data-testid="partner-form-domains"
        />
        <Hint>https://·포트·경로 없이 호스트명만 적습니다. 하위 도메인도 허용됩니다.</Hint>
        {errors.callbackDomains && <ErrorMsg>{errors.callbackDomains}</ErrorMsg>}
      </Field>

      <Field>
        <FieldLabel>자동 전달 주소 (선택)</FieldLabel>
        <TextInput
          value={provisionUrl}
          onChange={(e) => setProvisionUrl(e.target.value)}
          placeholder="https://kmrs-api.ymyd.co.kr/v1/webhooks/phai/credentials"
          data-testid="partner-form-provision-url"
        />
        <Hint>
          새 연동 키·서명 키를 상대 서버에 바로 넣을 주소입니다. 우리 서버인 파트너에만 씁니다. 호스트는 콜백
          허용 도메인 안이어야 합니다.
        </Hint>
        {errors.provisionUrl && <ErrorMsg>{errors.provisionUrl}</ErrorMsg>}
      </Field>

      <CheckLabel>
        <input
          type="checkbox"
          checked={autoReview}
          onChange={(e) => setAutoReview(e.target.checked)}
          data-testid="partner-form-auto-review"
        />
        수집 후 자동 분석
      </CheckLabel>

      <ModalFooter>
        <Button type="Text" size="Medium" variant="Ghost" color="Secondary" onClick={onCancel} disabled={submitting}>
          취소
        </Button>
        <Button
          type="Text"
          size="Medium"
          variant="Contained"
          color="Primary"
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="partner-form-submit"
        >
          {isEdit ? "저장" : "등록"}
        </Button>
      </ModalFooter>
    </ModalBody>
  );
};

export default PartnerFormModal;
